/**
 * A workout in progress, as Strong's log: per exercise a list of sets with values and a tick, plus the rest timer
 * and the set timer (countdown for `timed` and `hold` sets, stopwatch for `distance` sets).
 *
 * Everything is pure and driven by timestamps (ms since the epoch), never by counting intervals: the screen calls
 * `advance(workout, exercises, now, lastNow)` a few times per second, which performs what became due (a countdown
 * ended, a rest ended, the next interval set starts) and returns the cues (beeps, call-outs) that fell in between. After
 * the app was closed or the phone slept, the same call catches up.
 *
 * Exercises and sets carry random `key`s so the timers keep pointing at the right set while sets are added or removed.
 */
import { asEntry, entryMeasure, hasVariants, requiredFields, setFields, withMeasure, type Exercise, type Variant } from "./exercises";
import { sessionTemplate, volume, VALUE_KEYS, type Session, type SessionSet, type SetValues } from "./sessions";
import { ACTIVE_WORKOUT, META, newId, type Backend } from "./storage";
import {
  addExercises, cleanSet, cleanTemplate, defaultSet, newTemplateExercise, optionalTargets, type SetType, type Template,
  type TemplateExercise, type TemplateSet,
} from "./templates";

export interface WorkoutSet extends SetValues {
  key: string;
  type: SetType;
  done: boolean;
  /**
   * The template's optional targets of this set (Ziel-Reps of a `timed` set, Ziel-Zeit of a `distance` set). They
   * are goals, not values: shown by the timer and kept when the template is updated.
   */
  goal?: { reps?: number; durationSec?: number };
  /**
   * The reps were counted by the motion sensor (todo 8): the set's recording (collection RECORDINGS) and what the
   * sensor counted. When the workout ends the recording gets the reps as they stand then (`settleSensorSets`).
   */
  sensor?: { recordingId: string; counted: number };
}

export interface WorkoutExercise {
  key: string;
  exerciseId: string;
  /** the variant the exercise is done in (todo 16); absent: the glossary's measure */
  measure?: Variant;
  /** rest after each set, in seconds */
  restSec: number;
  /** "Intervall": after the rest following a timer, the next set's countdown starts by itself */
  interval: boolean;
  sets: WorkoutSet[];
}

export interface Rest {
  exKey: string;
  setKey: string;
  startedAt: number;
  endsAt: number;
  /** the set was finished by its timer (then "Pause" is spoken) */
  afterTimer: boolean;
  /** start the exercise's next open set when the rest ends (interval) */
  chain: boolean;
}

export interface SetTimer {
  exKey: string;
  setKey: string;
  /** "count": a rep set counted by the sensor (todo 17), open-ended until "Fertig"; it records no time */
  kind: "countdown" | "stopwatch" | "count";
  /** when the work starts; before that is the lead-in (3-2-1) */
  startedAt: number;
  /** length of a countdown (0 for the stopwatch) */
  durationSec: number;
  /** a `timed` set: taps count reps */
  counting: boolean;
  reps: number;
  /** the motion sensor counts the reps (todo 8): no taps, the phone is in the pocket */
  sensor?: boolean;
  /** a "count" set: "Vorher", whose values fill the row's empty ones when it ends (as ✓ does) */
  fill?: SetValues;
  pausedAt?: number;
}

export interface Workout {
  id: string;
  name: string;
  templateId?: string;
  startedAt: number;
  notes: string;
  exercises: WorkoutExercise[];
  rest?: Rest;
  timer?: SetTimer;
}

const SEC = 1000;
/** 3-2-1 before a set started with ▶ (an interval set started after a rest has the rest's 3-2-1 instead). */
export const LEAD_IN_MS = 3 * SEC;
/** A countdown for a set without a duration. */
export const DEFAULT_SET_SEC = 40;
/** "Noch 10 Sekunden" is said in sets and rests at least this long. */
export const TEN_LEFT_MIN_SEC = 20;
export const REST_STEP_SEC = 15;

type Measured = Pick<Exercise, "equipment" | "measure">;
const find = (exercises: Exercise[], id: string) => exercises.find((e) => e.id === id);
/** The exercise of an entry, in the entry's variant. */
export const entryExercise = (exercises: Exercise[], x: { exerciseId: string; measure?: Variant }) => asEntry(find(exercises, x.exerciseId), x);
const ALL_FIELDS = { weight: true, reps: true, duration: true, distance: true };

/** Exercises whose sets have a countdown can be run as intervals; `timed` (AMRAP) ones are by default. */
export const canInterval = (e: Measured | undefined) => e?.measure === "timed" || e?.measure === "hold";

// --- starting and editing ---

/** The template's sets as open workout sets; optional targets become goals. */
export function workoutSets(te: TemplateExercise, e: Measured | undefined): WorkoutSet[] {
  const opt = e ? optionalTargets(e) : { reps: false, duration: false };
  return te.sets.map((s) => {
    const { type, ...values } = cleanSet(s, e);
    const set: WorkoutSet = { key: newId(), type, done: false, ...values };
    if (opt.reps && set.reps !== undefined) { set.goal = { ...set.goal, reps: set.reps }; delete set.reps; }
    if (opt.duration && set.durationSec !== undefined) { set.goal = { ...set.goal, durationSec: set.durationSec }; delete set.durationSec; }
    return set;
  });
}

function workoutExercise(te: TemplateExercise, glossary: Exercise | undefined): WorkoutExercise {
  const e = asEntry(glossary, te);
  const x: WorkoutExercise = { key: newId(), exerciseId: te.exerciseId, restSec: te.restSec, interval: e?.measure === "timed", sets: workoutSets(te, e) };
  return withMeasure(te.measure ? { ...x, measure: te.measure } : x, glossary, te.measure);
}

/** A new workout, from a template or empty. */
export function startWorkout(opts: { template?: Template; exercises: Exercise[]; name: string; now: number }): Workout {
  const tpl = opts.template;
  return {
    id: newId(),
    name: tpl?.name ?? opts.name,
    ...(tpl ? { templateId: tpl.id } : {}),
    startedAt: opts.now,
    notes: "",
    exercises: tpl ? tpl.exercises.map((te) => workoutExercise(te, find(opts.exercises, te.exerciseId))) : [],
  };
}

/** "Übung hinzufügen": appends the exercises with one default set each (they also go into the template, `addToTemplate`). */
/**
 * "Wiederholen" in the history: a new workout with the session's exercises and sets, the values done as the starting
 * values. It is not linked to a template (finishing it asks no "Vorlage aktualisieren?").
 */
export function repeatWorkout(s: Session, exercises: Exercise[], now: number): Workout {
  const { templateId: _, ...w } = startWorkout({ template: { ...sessionTemplate(s, exercises), id: "" }, exercises, name: s.name, now });
  return w;
}

export function addWorkoutExercises(w: Workout, list: Exercise[]): Workout {
  const added = list.map((e) => workoutExercise(newTemplateExercise(e), e));
  return { ...w, exercises: [...w.exercises, ...added] };
}

/** Drops timers pointing at sets or exercises that are gone. */
function dropDangling(w: Workout): Workout {
  const exists = (r: { exKey: string; setKey: string } | undefined) =>
    !!r && !!w.exercises.find((x) => x.key === r.exKey)?.sets.some((s) => s.key === r.setKey);
  if ((!w.rest || exists(w.rest)) && (!w.timer || exists(w.timer))) return w;
  const out = { ...w };
  if (!exists(w.rest)) delete out.rest;
  if (!exists(w.timer)) delete out.timer;
  return out;
}

/**
 * "Art" (todo 16): the exercise done as the other variant, only while none of its sets is ticked. To `timed` the sets
 * get a duration (the last one used with the exercise, or 40 s), their reps become Ziel-Reps and "Intervall" is
 * switched on (as for an AMRAP added from the glossary); to `reps` the Ziel-Reps become the planned reps and the
 * durations go. A timer or rest of the exercise is dropped.
 */
export function switchWorkoutMeasure(w: Workout, exercises: Exercise[], exKey: string, to: Variant, durationSec: number): Workout {
  const x = w.exercises.find((y) => y.key === exKey);
  const glossary = x && find(exercises, x.exerciseId);
  if (!x || !glossary || !hasVariants(glossary) || entryMeasure(glossary, x) === to || x.sets.some((s) => s.done)) return w;
  const sets = x.sets.map((s): WorkoutSet => {
    const { goal, ...rest } = s;
    const out: WorkoutSet = { ...rest };
    if (to === "timed") {
      out.durationSec = s.durationSec ?? durationSec;
      const reps = s.reps ?? goal?.reps;
      delete out.reps;
      if (reps !== undefined) out.goal = { reps };
    } else {
      delete out.durationSec;
      if (s.reps === undefined && goal?.reps !== undefined) out.reps = goal.reps;
    }
    return out;
  });
  const next = withMeasure({ ...x, sets, interval: to === "timed" }, glossary, to);
  const out = { ...w, exercises: w.exercises.map((y) => (y.key === exKey ? next : y)) };
  if (w.timer?.exKey === exKey) delete out.timer;
  if (w.rest?.exKey === exKey) delete out.rest;
  return out;
}

export function removeWorkoutExercise(w: Workout, exKey: string): Workout {
  return dropDangling({ ...w, exercises: w.exercises.filter((x) => x.key !== exKey) });
}

export function updateWorkoutExercise(w: Workout, exKey: string, patch: Partial<Pick<WorkoutExercise, "restSec" | "interval">>): Workout {
  return { ...w, exercises: w.exercises.map((x) => (x.key === exKey ? { ...x, ...patch } : x)) };
}

const mapSets = (w: Workout, exKey: string, fn: (sets: WorkoutSet[]) => WorkoutSet[]): Workout =>
  ({ ...w, exercises: w.exercises.map((x) => (x.key === exKey ? { ...x, sets: fn(x.sets) } : x)) });

/** "+ Satz": a copy of the last set's values and goal as an open normal set, as in the template editor. */
export function addWorkoutSet(w: Workout, exKey: string, e: Measured | undefined): Workout {
  return mapSets(w, exKey, (sets) => {
    const last = sets[sets.length - 1];
    // the copy is a new set: the last one's sensor recording stays with it
    const set: WorkoutSet = last
      ? { ...last, key: newId(), type: "normal", done: false }
      : workoutSets({ exerciseId: "", restSec: 0, sets: [defaultSet(e ?? { measure: "reps" })] }, e)[0];
    delete set.sensor;
    return [...sets, set];
  });
}

export function removeWorkoutSet(w: Workout, exKey: string, setKey: string): Workout {
  return dropDangling(mapSets(w, exKey, (sets) => sets.filter((s) => s.key !== setKey)));
}

const fieldKeys = (e: Measured | undefined) => {
  const f = e ? setFields(e) : ALL_FIELDS;
  return VALUE_KEYS.filter((k) => ({ weightKg: f.weight, reps: f.reps, durationSec: f.duration, distanceM: f.distance })[k]);
};

const valid = (v: number | undefined) => v === undefined || (Number.isFinite(v) && v >= 0);

/** The values that are missing or invalid for ticking the set (after empty ones were taken from "Vorher"). */
export function missingForTick(set: SetValues, e: Measured | undefined, previous?: SetValues): (keyof SetValues)[] {
  const filled = fillFromPrevious(set, e, previous);
  return VALUE_KEYS.filter((k) => !valid(filled[k]) || (requiredFields(e).includes(k) && filled[k] === undefined));
}

/** Empty values (not invalid ones) are taken from "Vorher", as Strong takes the grey placeholder values. */
function fillFromPrevious<T extends SetValues>(set: T, e: Measured | undefined, previous?: SetValues): T {
  if (!previous) return set;
  const out = { ...set };
  for (const k of fieldKeys(e)) if (out[k] === undefined && previous[k] !== undefined) out[k] = previous[k];
  return out;
}

/** "Vorher" tapped: its values are copied into the set (the tick stays as it is). */
export function copyPrevious(w: Workout, exKey: string, setKey: string, e: Measured | undefined, previous: SetValues): Workout {
  const values: SetValues = {};
  for (const k of fieldKeys(e)) if (previous[k] !== undefined) values[k] = previous[k];
  return updateWorkoutSet(w, exKey, setKey, values, e);
}

/** Changes values of a set. A ticked set whose values become incomplete or invalid is unticked. */
export function updateWorkoutSet(w: Workout, exKey: string, setKey: string, patch: Partial<Omit<WorkoutSet, "key">>, e: Measured | undefined): Workout {
  return mapSets(w, exKey, (sets) => sets.map((s) => {
    if (s.key !== setKey) return s;
    const next = { ...s, ...patch };
    for (const k of VALUE_KEYS) if (k in patch && patch[k] === undefined) delete next[k];
    if (next.done && missingForTick(next, e).length) next.done = false;
    return next;
  }));
}

/**
 * ✓ tapped. Ticking takes empty values from "Vorher", marks the set done and starts the rest timer; nothing happens
 * if values are missing (`missingForTick` says which). Unticking stops the rest that followed the set.
 */
export function toggleSet(w: Workout, exercises: Exercise[], exKey: string, setKey: string, now: number, previous?: SetValues): Workout {
  const x = w.exercises.find((y) => y.key === exKey);
  const set = x?.sets.find((s) => s.key === setKey);
  if (!x || !set) return w;
  if (set.done) {
    const out = mapSets(w, exKey, (sets) => sets.map((s) => (s.key === setKey ? { ...s, done: false } : s)));
    if (w.rest?.setKey === setKey) delete out.rest;
    return out;
  }
  const e = entryExercise(exercises, x);
  if (missingForTick(set, e, previous).length) return w;
  const done = mapSets(w, exKey, (sets) => sets.map((s) => (s.key === setKey ? { ...fillFromPrevious(s, e, previous), done: true } : s)));
  return startRest(done, x, setKey, now, false, false);
}

// --- rest ---

function startRest(w: Workout, x: WorkoutExercise, setKey: string, at: number, afterTimer: boolean, chain: boolean): Workout {
  if (x.restSec <= 0 && !chain) {
    const { rest: _, ...out } = w;
    return out;
  }
  return { ...w, rest: { exKey: x.key, setKey, startedAt: at, endsAt: at + Math.max(0, x.restSec) * SEC, afterTimer, chain } };
}

/** ±15 s on the running rest; it never ends in the past. */
export function adjustRest(w: Workout, deltaSec: number, now: number): Workout {
  if (!w.rest) return w;
  return { ...w, rest: { ...w.rest, endsAt: Math.max(now, w.rest.endsAt + deltaSec * SEC) } };
}

/** "Überspringen": ends the rest now; in an interval the next set starts after the lead-in. */
export const skipRest = (w: Workout, exercises: Exercise[], now: number) => endRest(w, exercises, now, true);

/** "Intervall beenden": the rest keeps running, but the next set will not start by itself. */
export function stopChain(w: Workout): Workout {
  return w.rest?.chain ? { ...w, rest: { ...w.rest, chain: false } } : w;
}

function endRest(w: Workout, exercises: Exercise[], at: number, leadIn: boolean): Workout {
  const r = w.rest;
  if (!r) return w;
  const { rest: _, ...out } = w;
  if (!r.chain) return out;
  const next = nextIntervalSet(out, r.exKey, r.setKey);
  return next ? startTimer(out, exercises, r.exKey, next.key, at, leadIn) : out;
}

/** The first open set after the given one in the same exercise. */
export function nextIntervalSet(w: Workout, exKey: string, setKey: string): WorkoutSet | undefined {
  const sets = w.exercises.find((x) => x.key === exKey)?.sets ?? [];
  const i = sets.findIndex((s) => s.key === setKey);
  return sets.slice(i + 1).find((s) => !s.done);
}

// --- set timer ---

/**
 * ▶ on a `timed` or `hold` set (countdown of its duration), on a `distance` set (stopwatch), or on a `reps` set
 * counted by the sensor (todo 17: "count", open-ended; `previous` is "Vorher"). Starting a set ends the rest. With
 * `leadIn` the work starts after 3-2-1.
 */
export function startTimer(w: Workout, exercises: Exercise[], exKey: string, setKey: string, now: number, leadIn = true, previous?: SetValues): Workout {
  const x = w.exercises.find((y) => y.key === exKey);
  const set = x?.sets.find((s) => s.key === setKey);
  if (!x || !set) return w;
  const e = entryExercise(exercises, x);
  const kind = e?.measure === "distance" ? "stopwatch" : e?.measure === "reps" ? "count" : "countdown";
  const d = set.durationSec;
  const durationSec = kind !== "countdown" ? 0 : d !== undefined && Number.isFinite(d) && d > 0 ? d : DEFAULT_SET_SEC;
  const { rest: _, ...out } = w;
  const timer: SetTimer = {
    exKey, setKey, kind, startedAt: now + (leadIn ? LEAD_IN_MS : 0), durationSec, counting: e?.measure === "timed" || kind === "count", reps: 0,
  };
  if (kind === "count" && previous) timer.fill = previous;
  return { ...out, timer };
}

/** Where a timer stands: in the lead-in, or working with ms elapsed (and remaining for a countdown). */
export function timerState(tm: SetTimer, now: number) {
  const t = tm.pausedAt ?? now;
  const elapsed = t - tm.startedAt;
  return {
    phase: elapsed < 0 ? ("ready" as const) : ("work" as const),
    /** ms until the work starts (lead-in) */
    toStart: Math.max(0, -elapsed),
    elapsed: Math.max(0, elapsed),
    remaining: tm.kind === "countdown" ? Math.max(0, tm.durationSec * SEC - Math.max(0, elapsed)) : 0,
  };
}

/** A tap on the big area: +1 rep (−1 is undo). Taps count only while the set runs. */
export function tapRep(w: Workout, delta: 1 | -1, now: number): Workout {
  const tm = w.timer;
  if (!tm?.counting || tm.sensor || tm.pausedAt !== undefined) return w;
  if (delta > 0 && now < tm.startedAt) return w;
  return { ...w, timer: { ...tm, reps: Math.max(0, tm.reps + delta) } };
}

/** The sensor counts this set's reps from now on (`on`), or stopped doing so; only for the timer of that set. */
export function sensorTimer(w: Workout, exKey: string, setKey: string, on: boolean): Workout {
  const tm = w.timer;
  if (!tm?.counting || tm.exKey !== exKey || tm.setKey !== setKey || !!tm.sensor === on) return w;
  const { sensor: _, ...rest } = tm;
  return { ...w, timer: on ? { ...rest, sensor: true } : rest };
}

/** The sensor's count so far, shown by the timer and written into the row when the set ends. */
export function sensorReps(w: Workout, exKey: string, setKey: string, reps: number): Workout {
  const tm = w.timer;
  if (!tm?.sensor || tm.exKey !== exKey || tm.setKey !== setKey || tm.reps === reps) return w;
  return { ...w, timer: { ...tm, reps } };
}

/** A finished sensor set keeps its recording's id and the count (see `WorkoutSet.sensor`). */
export function markSensorSet(w: Workout, exKey: string, setKey: string, sensor: { recordingId: string; counted: number }): Workout {
  return mapSets(w, exKey, (sets) => sets.map((s) => (s.key === setKey ? { ...s, sensor } : s)));
}

export function pauseTimer(w: Workout, now: number): Workout {
  return w.timer && w.timer.pausedAt === undefined ? { ...w, timer: { ...w.timer, pausedAt: now } } : w;
}

export function resumeTimer(w: Workout, now: number): Workout {
  const tm = w.timer;
  if (tm?.pausedAt === undefined) return w;
  const { pausedAt, ...rest } = tm;
  return { ...w, timer: { ...rest, startedAt: tm.startedAt + (now - pausedAt) } };
}

/** "✕": the set stays open, nothing is recorded. */
export function cancelTimer(w: Workout): Workout {
  const { timer: _, ...out } = w;
  return out;
}

/**
 * The set's timer ends (countdown at 0, or "Fertig"/"Stopp" earlier): the time done and the counted reps go into the
 * row, the row is ticked and the rest starts (chained in interval mode). Stopped during the lead-in it is cancelled.
 * A row still missing a value (a distance set without metres) gets the time but stays open. A "count" set records
 * no time; its empty values (the weight) come from "Vorher", as when ✓ is tapped.
 */
export function finishTimer(w: Workout, exercises: Exercise[], now: number): Workout {
  const tm = w.timer;
  const x = tm && w.exercises.find((y) => y.key === tm.exKey);
  const set = x?.sets.find((s) => s.key === tm?.setKey);
  if (!tm || !x || !set) return cancelTimer(w);
  const end = tm.kind === "countdown" ? tm.startedAt + tm.durationSec * SEC : Infinity;
  const stop = tm.pausedAt ?? Math.min(now, end);
  if (stop < tm.startedAt) return cancelTimer(w);
  const values: SetValues = tm.kind === "count" ? {} : { durationSec: Math.round((stop - tm.startedAt) / SEC) };
  // no taps: a value typed into the row is kept
  if (tm.counting && (tm.reps > 0 || set.reps === undefined)) values.reps = tm.reps;
  const e = entryExercise(exercises, x);
  const filled = fillFromPrevious({ ...set, ...values }, e, tm.fill);
  const complete = missingForTick(filled, e).length === 0;
  const done = mapSets(cancelTimer(w), x.key, (sets) => sets.map((s) => (s.key === set.key ? { ...filled, done: complete } : s)));
  if (!complete) return done;
  const restAt = tm.pausedAt !== undefined ? now : stop;
  return startRest(done, x, set.key, restAt, true, tm.kind === "countdown" && x.interval);
}

// --- time passing ---

/** A sound or a spoken word at a point in time. */
export interface Cue {
  at: number;
  beep?: "count" | "go" | "end";
  /** a moment for an announcer call-out (todo 10); not played yet, the owner wants no speech */
  call?: "go" | "rest" | "tenLeft";
}

/** When the current timer or rest ends by itself (a stopwatch or a paused timer does not). */
export function nextTransition(w: Workout): number | undefined {
  const tm = w.timer;
  if (tm) return tm.kind === "countdown" && tm.pausedAt === undefined ? tm.startedAt + tm.durationSec * SEC : undefined;
  return w.rest?.endsAt;
}

/** All cues of the running timer or rest: 3-2-1 and "Los" before a set, "Noch 10 Sekunden" and 3-2-1 before an end. */
export function cuesOf(w: Workout): Cue[] {
  const cues: Cue[] = [];
  const countdownTo = (end: number, start: number) => {
    if (end - start >= TEN_LEFT_MIN_SEC * SEC) cues.push({ at: end - 10 * SEC, call: "tenLeft" });
    for (const k of [3, 2, 1]) if (end - k * SEC > start) cues.push({ at: end - k * SEC, beep: "count" });
  };
  const tm = w.timer;
  if (tm) {
    if (tm.pausedAt !== undefined) return cues;
    for (const k of [3, 2, 1]) cues.push({ at: tm.startedAt - k * SEC, beep: "count" });
    cues.push({ at: tm.startedAt, beep: "go", call: "go" });
    if (tm.kind === "countdown") {
      const end = tm.startedAt + tm.durationSec * SEC;
      countdownTo(end, tm.startedAt);
      cues.push({ at: end, beep: "end" });
    }
    return cues;
  }
  const r = w.rest;
  if (r) {
    if (r.afterTimer) cues.push({ at: r.startedAt, call: "rest" });
    countdownTo(r.endsAt, r.startedAt);
    // a chained rest ends with the next set's "Los"
    if (!(r.chain && nextIntervalSet(w, r.exKey, r.setKey))) cues.push({ at: r.endsAt, beep: "end" });
  }
  return cues;
}

/**
 * Performs everything due up to `now` and returns the cues in (`from`, `now`]. Several steps can be due at once
 * after the app slept; the caller drops cues that are too old to be useful.
 */
export function advance(w: Workout, exercises: Exercise[], now: number, from: number): { workout: Workout; cues: Cue[] } {
  let cur = w;
  const cues: Cue[] = [];
  for (let guard = 0; guard < 1000; guard++) {
    const at = nextTransition(cur);
    const until = at !== undefined && at <= now ? at : now;
    for (const c of cuesOf(cur)) if (c.at > from && c.at <= until) cues.push(c);
    if (at === undefined || at > now) break;
    cur = cur.timer ? finishTimer(cur, exercises, at) : endRest(cur, exercises, at, false);
    // the new state's cues start at `at`; the old state's cues up to `at` are taken
    from = Math.max(from, at - 1);
  }
  return { workout: cur, cues };
}

/** Cues older than this when `advance` returns them (the app was asleep) are not played any more. */
export const STALE_MS = 1500;

/**
 * Splits the cues of an `advance` at `now` into the ones still worth playing and the ones the app slept through
 * (hidden page on the iPhone, frozen app on Android): `missed` non-empty means chimes were not heard and the screen
 * says so (todo 14).
 */
export function dueCues(cues: Cue[], now: number): { play: Cue[]; missed: Cue[] } {
  const play: Cue[] = [], missed: Cue[] = [];
  for (const c of cues) (c.at >= now - STALE_MS ? play : missed).push(c);
  return { play, missed };
}

// --- finishing ---

export const countSets = (w: Workout, done: boolean) => w.exercises.reduce((n, x) => n + x.sets.filter((s) => s.done === done).length, 0);

/** The session to store: unticked sets are dropped (the screen asks first), and exercises left without sets. */
export function finishWorkout(w: Workout, now: number): Session {
  const exercises = w.exercises
    .map((x) => ({
      exerciseId: x.exerciseId,
      ...(x.measure ? { measure: x.measure } : {}),
      restSec: x.restSec,
      sets: x.sets.filter((s) => s.done).map((s): SessionSet => {
        const out: SessionSet = { type: s.type, done: true };
        for (const k of VALUE_KEYS) if (s[k] !== undefined && valid(s[k])) out[k] = s[k];
        return out;
      }),
    }))
    .filter((x) => x.sets.length > 0);
  return {
    id: w.id, name: w.name, ...(w.templateId ? { templateId: w.templateId } : {}),
    startedAt: w.startedAt, endedAt: now, notes: w.notes.trim(), exercises,
  };
}

export const workoutVolume = (w: Workout) => volume(w.exercises);

/**
 * "Template aktualisieren?": the template with this workout's exercises in their order, rest times and all their
 * sets (owner, Q11): done sets with the values done, open sets with their planned values; sets added or removed in
 * the workout are added or removed in the template. Optional targets stay goals. An exercise removed from the
 * workout leaves the template. Returns undefined when nothing differs.
 */
export function templateUpdate(tpl: Template, w: Workout, exercises: Exercise[]): Template | undefined {
  const next: TemplateExercise[] = w.exercises.map((x) => {
    const e = entryExercise(exercises, x);
    const opt = e ? optionalTargets(e) : { reps: false, duration: false };
    return {
      exerciseId: x.exerciseId,
      ...(x.measure ? { measure: x.measure } : {}),
      restSec: x.restSec,
      sets: x.sets.map((s) => {
        const set: TemplateSet = { type: s.type };
        for (const k of VALUE_KEYS) if (s[k] !== undefined && valid(s[k])) set[k] = s[k];
        if (opt.reps) set.reps = s.goal?.reps;
        if (opt.duration) set.durationSec = s.goal?.durationSec;
        return set;
      }),
    };
  });
  const updated = cleanTemplate({ ...tpl, exercises: next }, exercises) as Template;
  return JSON.stringify(updated) === JSON.stringify(cleanTemplate(tpl, exercises)) ? undefined : updated;
}

// --- storage ---

type ActiveRecord = { id: string; workout: Workout };

export async function loadActiveWorkout(db: Backend): Promise<Workout | undefined> {
  return (await db.get<ActiveRecord>(META, ACTIVE_WORKOUT))?.workout;
}

export const saveActiveWorkout = (db: Backend, workout: Workout) => db.put(META, { id: ACTIVE_WORKOUT, workout } as ActiveRecord);
export const clearActiveWorkout = (db: Backend) => db.delete(META, ACTIVE_WORKOUT);

/**
 * Exercises added during a workout go into its template right away (owner, Q5). False when there is no template
 * (any more).
 */
export async function addToTemplate(db: Backend, templateId: string | undefined, list: Exercise[]): Promise<boolean> {
  const tpl = templateId ? await db.get<Template>("templates", templateId) : undefined;
  if (!tpl) return false;
  await db.put("templates", addExercises(tpl, list) as Template);
  return true;
}
